import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next } from 'react-i18next'

import en from './en/translations.json'

export const DEFAULT_LANGUAGE = 'en'

const LANGUAGE_STORAGE_KEY = 'i18nextLng'

/**
 * Single source of truth for the languages the app ships with.
 * Labels are intentionally kept in their own language (endonyms) so the
 * switcher is readable regardless of the currently active locale.
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
] as const

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code']

// eslint-disable-next-line @typescript-eslint/no-floating-promises
i18n
  // Lazily loads every non-bundled locale as its own chunk via a dynamic import.
  // Vite code-splits `./<lng>/translations.json`, so only the active locale is
  // fetched over the network.
  .use(
    resourcesToBackend(async (language: string) => await import(`./${language}/translations.json`)),
  )
  // Detects the language from a previous choice (localStorage) or the browser,
  // and persists the selection so it survives reloads.
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // `en` is bundled eagerly so the fallback is always available synchronously
    // (no flash of translation keys on first paint). Other locales are lazy.
    resources: { en: { translation: en } },
    partialBundledLanguages: true,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map(({ code }) => code),
    // Map regional variants (e.g. `de-DE`) onto the base locale (`de`).
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    // No Suspense boundary wraps the app; disabling it keeps rendering stable
    // while a locale chunk is in flight (the fallback text is shown until ready).
    react: { useSuspense: false },
  })

i18n.services.formatter?.add('lowercase', (value: string, lng?: string) =>
  value.toLocaleLowerCase(lng),
)
